
import XLSX from "xlsx-js-style";

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: "000000" }, sz: 11 },
  fill: { fgColor: { rgb: "7B9CFF" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: "FFFFFF" } },
    bottom: { style: "thin", color: { rgb: "FFFFFF" } },
    left: { style: "thin", color: { rgb: "FFFFFF" } },
    right: { style: "thin", color: { rgb: "FFFFFF" } },
  },
};

const bodyStyle = (rowIndex) => ({
  alignment: { vertical: "center", wrapText: true },
  fill: { fgColor: { rgb: rowIndex % 2 === 0 ? "FFFFFF" : "F2F5FF" } },
  border: {
    top: { style: "thin", color: { rgb: "E0E0E0" } },
    bottom: { style: "thin", color: { rgb: "E0E0E0" } },
    left: { style: "thin", color: { rgb: "E0E0E0" } },
    right: { style: "thin", color: { rgb: "E0E0E0" } },
  },
});

function styleSheet(ws, headers) {
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let c = range.s.c; c <= range.e.c; c++) {
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell) continue;
      cell.s = r === 0 ? HEADER_STYLE : bodyStyle(r);
    }
  }
  ws["!cols"] = headers.map((h) => ({ wch: Math.min(Math.max(h.length + 2, 14), 40) }));
  ws["!rows"] = [{ hpt: 28 }]; // taller header row
}

const CARA_PLWD_COLUMNS = [
  { header: "PLWD", uid: "q-plwd-identifier" },
  { header: "First name", uid: "q-plwd-firstname" },
  { header: "Last Name", uid: "q-plwd-lastname" },
  { header: "Name to be printed on CARA card (max 14 characters)", uid: "q-plwd-caraname" },
  { header: "Last 4 char of NRIC no.", uid: "q-plwd-nric4" },
  { header: "Gender", uid: "q-plwd-gender" },
  { header: "Date of Birth", uid: "q-plwd-dob" },
  { header: "Contact no. (if available)", uid: "q-plwd-contact" },
  { header: "Email address (if available)", uid: "q-plwd-email" },
  { header: "Blk No.", uid: "q-plwd-blk" },
  { header: "Street Name", uid: "q-plwd-street" },
  { header: "Unit Number", uid: "q-plwd-unit" },
  { header: "Postal code", uid: "q-plwd-postal" },
  { header: "Type of dementia", uid: "q-plwd-dementia-type" },
  { header: "Stage of dementia", uid: "q-plwd-dementia-stage" },
  { header: "Symptoms of dementia", uid: "q-plwd-symptoms" },
  { header: "Preferred/Spoken language", uid: "q-plwd-language" },
  { header: "Hospital or clinic they visited (if available)", uid: "q-plwd-hospital" },
  { header: "Hospital department they visited (if available)", uid: "q-plwd-hospital-dept" },
];

const CARA_CG_COLUMNS = [
  { header: "PLWD", uid: "q-cg-plwd" },
  { header: "First name", uid: "q-cg-firstname" },
  { header: "Last Name", uid: "q-cg-lastname" },
  { header: "Last 4 char of NRIC no.", uid: "q-cg-nric4" },
  { header: "Gender", uid: "q-cg-gender" },
  { header: "Date of Birth", uid: "q-cg-dob" },
  { header: "Contact no.", uid: "q-cg-contact" },
  { header: "Email address", uid: "q-cg-email" },
  { header: "Blk No.", uid: "q-cg-blk" },
  { header: "Street Name", uid: "q-cg-street" },
  { header: "Unit Number", uid: "q-cg-unit" },
  { header: "Postal code", uid: "q-cg-postal" },
  { header: "How is caregiver related to PLWD?", uid: "q-cg-relationship" },
];

function cellValue(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(cellValue).join(", ");
  if (typeof value === "object") return value.label ?? value.value ?? JSON.stringify(value);
  return value;
}

function valueByUid(sections, uid) {
  if (!uid) return "";
  for (const section of sections || []) {
    for (const question of section.questions || []) {
      if (question.uid === uid) return cellValue(question.value);
    }
  }
  return "";
}

function parseSections(response) {
  try {
    return JSON.parse(response.crb5c_answerjson) || [];
  } catch {
    return [];
  }
}

function rowFromColumns(sections, columns) {
  const row = {};
  for (const col of columns) row[col.header] = valueByUid(sections, col.uid);
  return row;
}

// Generic single-sheet styled export. `rows` are plain objects keyed by header,
// `headers` fixes the column order. Used for the CARA status/tracking export.
export function exportStyledSheet(rows, headers, { sheetName = "Sheet1", fileName = "export.xlsx" } = {}) {
  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.json_to_sheet(rows, { header: headers });
  styleSheet(sheet, headers);
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  XLSX.writeFile(workbook, fileName);
}

export function exportCaraTemplate(responses, fileName = "CARA_Account_Creations.xlsx") {
  const plwdRows = [];
  const caregiverRows = [];
  for (const response of responses) {
    const sections = parseSections(response);
    plwdRows.push(rowFromColumns(sections, CARA_PLWD_COLUMNS));
    caregiverRows.push(rowFromColumns(sections, CARA_CG_COLUMNS));
  }

  const workbook = XLSX.utils.book_new();
  const plwdHeaders = CARA_PLWD_COLUMNS.map((c) => c.header);
  const cgHeaders = CARA_CG_COLUMNS.map((c) => c.header);
  const plwdSheet = XLSX.utils.json_to_sheet(plwdRows, { header: plwdHeaders });
  const cgSheet = XLSX.utils.json_to_sheet(caregiverRows, { header: cgHeaders });
  styleSheet(plwdSheet, plwdHeaders);
  styleSheet(cgSheet, cgHeaders);
  XLSX.utils.book_append_sheet(workbook, plwdSheet, "PLWD Details");
  XLSX.utils.book_append_sheet(workbook, cgSheet, "Caregiver Details");
  XLSX.writeFile(workbook, fileName);
}
